import React from 'react';
import { Calendar, Sparkles, Leaf, Award } from 'lucide-react';

const FestivalsEvents = () => {
  const events = [
  {
    id: 'araw-ng-taraw',
    title: 'Araw ng Taraw',
    icon: Calendar,
    date: 'Every March',
    year: '55th Anniversary (2025)',
    theme: 'Resilient @54 / Maskég: Stronger @55',
    image: "/assets/images/Araw_ng_Taraw-1769665467123.jpg",
    imageAlt: 'Aerial view of Sabang Wharf showing community celebration area with bancas and visitor facilities for Araw ng Taraw festival at PPSRNP',
    description: 'Every March, the Puerto Princesa Subterranean River National Park (PPSRNP) comes alive in celebration of Araw ng Taraw, an annual observance that honors both nature and tourism. The celebration commemorates March 26, 1971, when the national government officially declared the area as the Puerto Princesa Subterranean River National Park—globally known for its majestic Puerto Princesa Underground River (PPUR).',
    details: 'Araw ng Taraw takes its name from Mount Taraw, the towering limestone cliffs beneath which the underground river flows. "Taraw" symbolizes the rugged karst landscape that defines the park\'s Outstanding Universal Value and the natural strength that protects it. Rooted in environmental awareness and community participation, the celebration features various activities that promote conservation, cultural pride, and shared stewardship of the protected area. For 2025, the celebration carries the theme "Resilient @54," highlighting the enduring strength of the PPSRNP community in the face of uncertainties. Complementing this is the Indigenous term "Maskég: Stronger @55," a powerful reminder that regardless of calamities and challenges, the community continues to stand united.'
  },
  {
    id: 'subaraw',
    title: 'SUBARAW Biodiversity Festival',
    icon: Leaf,
    date: 'Annual Month-Long Celebration',
    image: "/assets/images/SUBARAW_Biodiversity_Festival-1769668385604.png",
    imageAlt: 'Community celebration at SUBARAW Biodiversity Festival showing people performing with marine-themed decorative streamers and costumes celebrating cultural heritage and environmental conservation',
    description: 'The SUBARAW Biodiversity Festival is an annual, month-long celebration that honors the rich biodiversity, culture, and natural heritage of Puerto Princesa. The name SUBARAW is derived from two Cuyunon terms: "Suba," meaning river—referring to the Puerto Princesa Underground River (PPUR), and "Taraw," which represents the iconic karst mountains of the Puerto Princesa Subterranean River National Park (PPSRNP).',
    details: 'The festival features a series of environmental activities that highlight the city\'s diverse ecosystems, community involvement, and commitment to conservation. Central to the celebration is the showcase of the Puerto Princesa Underground River, including the observance of Puerto Princesa Underground River Day, as declared under Presidential Proclamation No. 816 on November 11, 2011. This proclamation, issued during the administration of former President Benigno S. Aquino III, recognizes the PPUR as one of the New 7 Wonders of Nature, affirming its global significance and reinforcing Puerto Princesa\'s role as a leader in biodiversity conservation and sustainable tourism.'
  },
  {
    id: 'unesco-anniversary',
    title: 'UNESCO World Heritage List Inscription Anniversary',
    icon: Award,
    date: 'December 4',
    year: 'Since 1999',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fc179770-1769661307881.png",
    imageAlt: 'Recognition ceremony honoring conservation champions and partners for their contributions to PPSRNP protection and sustainable management',
    description: 'One of the annual celebrations of the Puerto Princesa Subterranean River National Park (PPSRNP) commemorates its inscription on the United Nations Educational, Scientific and Cultural Organization (UNESCO) World Heritage List on December 4, 1999. This milestone highlights the global commitment to the protection of World Cultural and Natural Heritage.',
    details: 'Inclusion in the World Heritage List affirms the park\'s Outstanding Universal Value, recognizing it as a cultural and natural site of exceptional importance that merits protection for the benefit of all humanity. The continued preservation of PPSRNP stands as a symbol of the enduring commitment of the people of Puerto Princesa, and of the Philippines as a whole, to the conservation of our natural heritage. The annual celebration also serves as a platform to recognize individuals, groups, and organizations that have made significant contributions to the protection, conservation, and sustainable management of the PPSRNP.'
  },
  {
    id: 'tourism-updates',
    title: 'Tourism Development Updates',
    icon: Sparkles,
    image: "/assets/images/Tourism_Development_Updates-1769668491901.JPG",
    imageAlt: 'Children in traditional blue and yellow cultural costumes at community celebration showcasing cultural heritage and conservation awareness at PPSRNP',
    description: 'In line with its vision to remain a model natural heritage site, the Puerto Princesa Underground River (PPUR) continues to implement key improvements focused on visitor experience, sustainability, and effective site management.',
    details: 'Among the recent developments is the rehabilitation of essential facilities, particularly the comfort rooms located at the Sabang Wharf and within the Puerto Princesa Underground River Complex. Complementing these physical upgrades, PPUR is undertaking the revision of tour procedures to further enhance safety, efficiency, and visitor flow. The management is also in the process of developing and launching the Official PPUR webpage, which will serve as a centralized platform for accurate information, updates, and visitor guidance. Daily management operations are likewise being strengthened to ensure the consistent delivery of quality services. In addition, the development of new signages and branding materials is a key highlight of this year\'s updates, reinforcing PPUR\'s identity while improving navigation and information dissemination throughout the site. Together, these initiatives reflect PPUR\'s continuing commitment to conservation, excellence, and responsible tourism.'
  }];


  return (
    <section className="py-16 bg-gradient-to-b from-[var(--color-background)] to-[var(--color-card)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-[var(--color-primary)] dark:text-[var(--color-tertiary)] mb-4">
            Festivals & Events
          </h2>
          <p className="text-lg text-gray-800 dark:text-gray-200 max-w-3xl mx-auto">
            Celebrate the rich cultural heritage and conservation milestones of PPSRNP through annual festivals and special events.
          </p>
        </div>

        {/* Events List */}
        <div className="space-y-8">
          {events?.map((event) => {
            const IconComponent = event?.icon;
            return (
              <div
                key={event?.id}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border border-[var(--color-border)]">

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Image */}
                  {event?.image &&
                  <div className="relative h-64 lg:h-auto overflow-hidden">
                      <img
                      src={event?.image}
                      alt={event?.imageAlt}
                      className="w-full h-full object-cover" />

                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      <div className="absolute bottom-4 left-4">
                        <div className="w-14 h-14 rounded-full bg-white/90 dark:bg-gray-800/90 flex items-center justify-center">
                          <IconComponent className="w-7 h-7 text-[var(--color-primary)] dark:text-[var(--color-tertiary)]" />
                        </div>
                      </div>
                    </div>
                  }

                  {/* Content */}
                  <div className="lg:col-span-2 p-8">
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <h3 className="text-2xl font-bold text-[var(--color-primary)] dark:text-[var(--color-tertiary)]">
                        {event?.title}
                      </h3>
                      <span className="px-3 py-1 bg-[var(--color-accent)]/20 text-[var(--color-accent)] dark:text-[var(--color-tertiary)] rounded-full text-sm font-semibold">
                        {event?.date}
                      </span>
                      {event?.year &&
                      <span className="px-3 py-1 bg-[var(--color-secondary)]/20 text-[var(--color-secondary)] dark:text-[var(--color-tertiary)] rounded-full text-sm font-semibold">
                          {event?.year}
                        </span>
                      }
                    </div>

                    {event?.theme &&
                    <div className="mb-4">
                        <span className="inline-block px-4 py-2 bg-[var(--color-primary)]/10 dark:bg-[var(--color-tertiary)]/10 rounded-lg text-[var(--color-primary)] dark:text-[var(--color-tertiary)] font-semibold">
                          Theme: {event?.theme}
                        </span>
                      </div>
                    }

                    <p className="text-gray-800 dark:text-gray-200 mb-4 leading-relaxed">
                      {event?.description}
                    </p>

                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-sm">
                      {event?.details}
                    </p>
                  </div>
                </div>
              </div>);

          })}
        </div>
      </div>
    </section>);

};

export default FestivalsEvents;